// Canonical money primitive for supa.place.
//
// A Money value is an integer count of a currency's smallest ISO 4217 unit
// ("minor units") tagged with its ISO 4217 currency code. All arithmetic is
// exact integer arithmetic within a single currency. Rounding happens ONLY at
// explicit boundaries: constructing from a decimal, multiplying by a rate, or
// splitting. Cross-currency arithmetic throws.
//
// Intentionally dependency-free so it runs in the browser, in Cloud Functions,
// and in a plain node script, and can be unit-tested without a build step.
// MIRRORED in src/lib/money.ts and functions/src/lib/money.ts — keep in sync.
// The Firestore/zod validation layer is server-side only and lives in
// functions/src/lib/money-schema.ts; the client never validates persisted money.

/** The 29 currencies supa.place supports. Mirrors src/lib/currency-data.ts. */
export const CURRENCY_CODES = [
  "USD", "EUR", "JPY", "GBP", "CNY", "AUD", "CAD", "CHF", "SGD", "HKD",
  "NZD", "SEK", "NOK", "DKK", "INR", "BRL", "ZAR", "MXN", "TRY", "PLN",
  "MYR", "THB", "IDR", "CZK", "HUF", "PHP", "RON", "ILS", "AED",
] as const;

export type CurrencyCode = (typeof CURRENCY_CODES)[number];

// Codes whose ISO 4217 minor unit is not 1/100. Covers the whole standard, not
// just the 29 we price in, because PAYOUT target currencies are raw ISO strings
// that deliberately never enter the ledger (see payout-transfer-provider.ts) and
// so can never go through currencyExponent, which is CurrencyCode-only.
const ZERO_DECIMAL_CURRENCIES = new Set([
  "BIF", "CLP", "DJF", "GNF", "ISK", "JPY", "KMF", "KRW", "PYG", "RWF",
  "UGX", "UYI", "VND", "VUV", "XAF", "XOF", "XPF",
]);
const THREE_DECIMAL_CURRENCIES = new Set([
  "BHD", "IQD", "JOD", "KWD", "LYD", "OMR", "TND",
]);

/**
 * ISO 4217 minor-unit exponent for ANY currency string, including ones outside
 * our priced CurrencyCode set. Unlike currencyExponent this never throws: an
 * unrecognised code falls back to 2, which is correct for the overwhelming
 * majority of ISO 4217 and is the only safe default at a provider boundary.
 *
 * Use this for amounts denominated in a currency we do not price in, such as a
 * seller's bank payout currency. Use currencyExponent for Money, which is only
 * ever constructed in a supported currency.
 */
export function iso4217Exponent(currency: string): number {
  const code = String(currency || "").toUpperCase();
  if (ZERO_DECIMAL_CURRENCIES.has(code)) return 0;
  if (THREE_DECIMAL_CURRENCIES.has(code)) return 3;
  return 2;
}

/**
 * ISO 4217 minor-unit exponents. The minor unit is 10^exponent of the major
 * unit: USD (2) has 100 cents per dollar; JPY (0) has no subunit.
 *
 * In our set only JPY is zero-decimal. HUF is 2 (fillér) per ISO 4217 even
 * though the forint is used in practice without subunits and processors
 * require HUF amounts to be whole-forint multiples of 100. That is a
 * provider-boundary quirk handled at the call site, NOT in this canonical
 * representation (see toProviderAmount).
 */
const CURRENCY_EXPONENT: Record<CurrencyCode, number> = {
  USD: 2, EUR: 2, JPY: 0, GBP: 2, CNY: 2, AUD: 2, CAD: 2, CHF: 2, SGD: 2,
  HKD: 2, NZD: 2, SEK: 2, NOK: 2, DKK: 2, INR: 2, BRL: 2, ZAR: 2, MXN: 2,
  TRY: 2, PLN: 2, MYR: 2, THB: 2, IDR: 2, CZK: 2, HUF: 2, PHP: 2, RON: 2,
  ILS: 2, AED: 2,
};

/** An amount of money: an integer count of `currency`'s smallest unit. */
export interface Money {
  /** Integer count of the currency's smallest unit. Always a safe integer. */
  readonly minorUnits: number;
  readonly currency: CurrencyCode;
}

/** Rounding strategies for the boundary operations (fromDecimal, multiply). */
export const RoundingMode = {
  /** .5 rounds away from zero. Default; matches most invoicing/tax rules. */
  HALF_UP: "HALF_UP",
  /** Banker's rounding: .5 rounds to the nearest even. Reduces cumulative bias. */
  HALF_EVEN: "HALF_EVEN",
  /** Truncate toward zero. */
  DOWN: "DOWN",
  /** Away from zero. */
  UP: "UP",
  /** Toward -Infinity. */
  FLOOR: "FLOOR",
  /** Toward +Infinity. */
  CEIL: "CEIL",
} as const;
export type RoundingMode = (typeof RoundingMode)[keyof typeof RoundingMode];

// --- Errors -----------------------------------------------------------------

export class MoneyError extends Error {}

export class CurrencyMismatchError extends MoneyError {
  constructor(a: CurrencyCode, b: CurrencyCode) {
    super(`Cannot combine Money of different currencies: ${a} and ${b}`);
    this.name = "CurrencyMismatchError";
  }
}

export class InvalidMoneyError extends MoneyError {
  constructor(message: string) {
    super(message);
    this.name = "InvalidMoneyError";
  }
}

// --- Currency helpers -------------------------------------------------------

export function isSupportedCurrency(code: unknown): code is CurrencyCode {
  return typeof code === "string" &&
    Object.prototype.hasOwnProperty.call(CURRENCY_EXPONENT, code);
}

/** ISO 4217 minor-unit exponent for a supported currency. */
export function currencyExponent(currency: CurrencyCode): number {
  // Cast so the undefined guard is meaningful at runtime for callers who reach
  // here with an unvalidated value (TS otherwise types the access as `number`).
  const exp = (CURRENCY_EXPONENT as Record<string, number | undefined>)[currency];
  if (exp === undefined) throw new InvalidMoneyError(`Unknown currency: ${String(currency)}`);
  return exp;
}

// --- Construction -----------------------------------------------------------

/** Construct from an integer minor-unit count. Throws on non-integer/unsafe/unknown currency. */
export function money(minorUnits: number, currency: CurrencyCode): Money {
  if (!isSupportedCurrency(currency)) {
    throw new InvalidMoneyError(`Unknown currency: ${String(currency)}`);
  }
  if (!Number.isSafeInteger(minorUnits)) {
    throw new InvalidMoneyError(
      `minorUnits must be a safe integer, got ${minorUnits} (${currency})`,
    );
  }
  // Normalise -0 to 0 so negate(zero)/rounding never yield a stray negative zero.
  return Object.freeze({ minorUnits: minorUnits === 0 ? 0 : minorUnits, currency });
}

/** The zero amount in a currency. */
export function zero(currency: CurrencyCode): Money {
  return money(0, currency);
}

/**
 * Construct from a major-unit decimal amount. Prefer a STRING for exactness
 * ("3.50"); a number is accepted and normalised through its decimal string so
 * float artefacts (0.1 + 0.2) do not leak in. Rounds any digits finer than the
 * currency's minor unit using `rounding`.
 */
export function fromDecimal(
  amount: number | string,
  currency: CurrencyCode,
  rounding: RoundingMode = RoundingMode.HALF_UP,
): Money {
  if (!isSupportedCurrency(currency)) {
    throw new InvalidMoneyError(`Unknown currency: ${String(currency)}`);
  }
  const exp = currencyExponent(currency);
  // For a number, expand its exact shortest-round-tripping decimal (never
  // toFixed, which rounds a second time and corrupts truncating modes). For a
  // string, scale as given. Either way scaleDecimalString rounds exactly once.
  const text = typeof amount === "number" ? numberToDecimalString(amount) : amount;
  return money(scaleDecimalString(text, exp, rounding), currency);
}

/**
 * Exact plain-decimal string for a finite number, expanding exponential notation
 * (String(1e-7) === "1e-7") so scaleDecimalString's parser can consume it and
 * round it exactly once.
 */
function numberToDecimalString(n: number): string {
  if (!Number.isFinite(n)) throw new InvalidMoneyError(`Amount must be finite, got ${n}`);
  const s = String(n);
  if (!/e/i.test(s)) return s;
  const negative = s.startsWith("-");
  const [mantissa, expPart] = s.replace(/^-/, "").split(/e/i);
  const exp = parseInt(expPart, 10);
  const dot = mantissa.indexOf(".");
  const digits = mantissa.replace(".", "");
  const pointIndex = (dot === -1 ? mantissa.length : dot) + exp;
  let out: string;
  if (pointIndex <= 0) {
    out = "0." + "0".repeat(-pointIndex) + digits;
  } else if (pointIndex >= digits.length) {
    out = digits + "0".repeat(pointIndex - digits.length);
  } else {
    out = digits.slice(0, pointIndex) + "." + digits.slice(pointIndex);
  }
  return (negative ? "-" : "") + out;
}

/** Parse a plain decimal string into a signed integer count of 10^exp units. */
function scaleDecimalString(input: string, exp: number, rounding: RoundingMode): number {
  const s = input.trim();
  if (!/\d/.test(s)) throw new InvalidMoneyError(`Not a decimal number: ${input}`);
  const m = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(s);
  if (!m) throw new InvalidMoneyError(`Not a decimal number: ${input}`);
  const negative = m[1] === "-";
  const intPart = m[2] || "0";
  const fracPart = m[3] || "";
  const kept = fracPart.slice(0, exp).padEnd(exp, "0");
  const rest = fracPart.slice(exp); // digits past the minor unit, to be rounded
  const magnitude = Number(intPart + kept);
  if (!Number.isSafeInteger(magnitude)) {
    throw new InvalidMoneyError(`Amount out of safe-integer range: ${input}`);
  }
  const bumped = shouldRoundUp(rest, rounding, negative, magnitude % 10)
    ? magnitude + 1
    : magnitude;
  return negative ? -bumped : bumped;
}

/**
 * Given the discarded fractional digits `rest`, decide whether the retained
 * magnitude should be incremented. Operates on magnitude; `negative` carries
 * the sign so FLOOR/CEIL resolve correctly. `keptLastDigit` is the last digit
 * of the retained magnitude, needed for HALF_EVEN ties.
 */
function shouldRoundUp(
  rest: string,
  mode: RoundingMode,
  negative: boolean,
  keptLastDigit: number,
): boolean {
  if (rest.length === 0 || !/[1-9]/.test(rest)) return false; // nothing dropped
  const first = rest.charCodeAt(0) - 48;
  const restNonZeroAfterFirst = /[1-9]/.test(rest.slice(1));
  switch (mode) {
    case RoundingMode.DOWN: return false;
    case RoundingMode.UP: return true;
    case RoundingMode.CEIL: return !negative;
    case RoundingMode.FLOOR: return negative;
    case RoundingMode.HALF_UP: return first >= 5;
    case RoundingMode.HALF_EVEN:
      if (first > 5) return true;
      if (first < 5) return false;
      if (restNonZeroAfterFirst) return true; // strictly greater than .5
      return keptLastDigit % 2 === 1; // exactly .5 -> round to even
    default: return false;
  }
}

// --- Accessors --------------------------------------------------------------

/** Exact decimal string in major units, e.g. money(350,'AUD') -> "3.50". */
export function toDecimalString(m: Money): string {
  const exp = currencyExponent(m.currency);
  const neg = m.minorUnits < 0;
  const abs = Math.abs(m.minorUnits).toString().padStart(exp + 1, "0");
  if (exp === 0) return (neg ? "-" : "") + abs;
  const cut = abs.length - exp;
  return (neg ? "-" : "") + abs.slice(0, cut) + "." + abs.slice(cut);
}

/**
 * Major-unit amount as a number. Lossy for very large amounts (float); use for
 * display and legacy interop only, never as a store of value.
 */
export function toDecimalNumber(m: Money): number {
  return m.minorUnits / 10 ** currencyExponent(m.currency);
}

export const isZero = (m: Money): boolean => m.minorUnits === 0;
export const isPositive = (m: Money): boolean => m.minorUnits > 0;
export const isNegative = (m: Money): boolean => m.minorUnits < 0;
export const sign = (m: Money): number => Math.sign(m.minorUnits);

// --- Arithmetic (single currency) -------------------------------------------

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) throw new CurrencyMismatchError(a.currency, b.currency);
}

export function add(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.minorUnits + b.minorUnits, a.currency);
}

export function subtract(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.minorUnits - b.minorUnits, a.currency);
}

export function negate(a: Money): Money {
  return money(-a.minorUnits, a.currency);
}

export function absolute(a: Money): Money {
  return money(Math.abs(a.minorUnits), a.currency);
}

/** Sum a list. Pass `currency` to define the result for a possibly-empty list. */
export function sum(monies: Money[], currency?: CurrencyCode): Money {
  if (monies.length === 0) {
    if (!currency) throw new InvalidMoneyError("sum([]) requires a currency");
    return zero(currency);
  }
  const cur = currency ?? monies[0].currency;
  let total = 0;
  for (const m of monies) {
    if (m.currency !== cur) throw new CurrencyMismatchError(cur, m.currency);
    total += m.minorUnits;
    // Guard every partial sum: a running total that crosses 2^53 and comes back
    // would otherwise pass the final safe-integer check at a corrupted value.
    if (!Number.isSafeInteger(total)) {
      throw new InvalidMoneyError("sum overflowed the safe-integer range");
    }
  }
  return money(total, cur);
}

/**
 * Multiply by a scalar (a rate, quantity, or fraction) and round to the minor
 * unit. This is a rounding boundary: money(35000,'AUD') * 0.035 -> A$12.25.
 * For splitting a total into parts that must sum exactly, use `allocate`.
 */
export function multiply(
  m: Money,
  factor: number,
  rounding: RoundingMode = RoundingMode.HALF_UP,
): Money {
  if (!Number.isFinite(factor)) {
    throw new InvalidMoneyError(`factor must be finite, got ${factor}`);
  }
  return money(roundToInteger(m.minorUnits * factor, rounding), m.currency);
}

/**
 * Convenience for a HUMAN per-hundred value: percentage(m, 2.5) = 2.5% of m.
 * For a stored fee/discount rate held as a fraction (0.025), use `applyRate`
 * or `multiply` instead — passing 0.025 here would compute 0.025%, a 100x error.
 */
export function percentage(
  m: Money,
  percent: number,
  rounding: RoundingMode = RoundingMode.HALF_UP,
): Money {
  return multiply(m, percent / 100, rounding);
}

/**
 * Apply a "fraction + fixed" fee/surcharge: base * fraction (+ fixed), rounded
 * once. `fraction` is a decimal fraction (0.035 = 3.5%), matching how the fee
 * tables store rates (TIER_FEES stores 0.035, not 3.5). Do NOT pass a
 * per-hundred value here. Funnels all rate+fixed fee math through one rounding
 * policy so call sites cannot silently diverge.
 */
export function applyRate(
  base: Money,
  rate: { fraction: number; fixed?: Money },
  rounding: RoundingMode = RoundingMode.HALF_UP,
): Money {
  const variable = multiply(base, rate.fraction, rounding);
  return rate.fixed ? add(variable, rate.fixed) : variable;
}

/**
 * Round to the nearest multiple of `step` minor units (e.g. HUF whole-forint =
 * step 100, which processors require). Rounds at the coarser granularity.
 */
export function roundToMultiple(
  m: Money,
  step: number,
  rounding: RoundingMode = RoundingMode.HALF_UP,
): Money {
  if (!Number.isInteger(step) || step <= 0) {
    throw new InvalidMoneyError(`step must be a positive integer, got ${step}`);
  }
  return money(roundToInteger(m.minorUnits / step, rounding) * step, m.currency);
}

function roundToInteger(value: number, mode: RoundingMode): number {
  if (Number.isInteger(value)) return value;
  const EPS = 1e-9;
  const negative = value < 0;
  const abs = Math.abs(value);
  let floor = Math.floor(abs);
  let frac = abs - floor;
  if (frac > 1 - EPS) {
    floor += 1;
    frac = 0; // snap up through float noise
  } else if (frac < EPS) {
    frac = 0;
  }
  let up: boolean;
  if (frac === 0) up = false;
  else switch (mode) {
    case RoundingMode.DOWN: up = false; break;
    case RoundingMode.UP: up = true; break;
    case RoundingMode.CEIL: up = !negative; break;
    case RoundingMode.FLOOR: up = negative; break;
    case RoundingMode.HALF_UP: up = frac >= 0.5 - EPS; break;
    case RoundingMode.HALF_EVEN:
      if (frac > 0.5 + EPS) up = true;
      else if (frac < 0.5 - EPS) up = false;
      else up = floor % 2 === 1;
      break;
    default: up = false;
  }
  const magnitude = floor + (up ? 1 : 0);
  const result = negative ? -magnitude : magnitude;
  if (!Number.isSafeInteger(result)) {
    throw new InvalidMoneyError(`Result out of safe-integer range: ${value}`);
  }
  return result;
}

/**
 * Split `m` into parts proportional to `weights`, distributing leftover minor
 * units by the largest-remainder method so the parts sum EXACTLY to `m` (no
 * lost pennies). Use for seller/platform/tax splits. Ties go to lower indices.
 */
export function allocate(m: Money, weights: number[]): Money[] {
  if (weights.length === 0) throw new InvalidMoneyError("allocate requires at least one weight");
  if (weights.some((w) => !Number.isFinite(w) || w < 0)) {
    throw new InvalidMoneyError("allocate weights must be finite and non-negative");
  }
  const total = weights.reduce((a, b) => a + b, 0);
  if (total <= 0) throw new InvalidMoneyError("allocate weights must sum to a positive number");

  const whole = m.minorUnits;
  const parts = weights.map((w) => {
    const exact = (whole * w) / total;
    const floor = Math.floor(exact);
    return { floor, frac: exact - floor };
  });
  const allocated = parts.reduce((a, p) => a + p.floor, 0);
  let remainder = whole - allocated; // integer count of +1s to hand out
  const order = parts
    .map((p, i) => ({ i, frac: p.frac }))
    .sort((a, b) => (b.frac - a.frac) || (a.i - b.i));
  const result = parts.map((p) => p.floor);
  for (let k = 0; remainder > 0 && k < order.length; k++, remainder--) {
    result[order[k].i] += 1;
  }
  const out = result.map((u) => money(u, m.currency));
  // Post-condition: the float share math above can drift near the safe-integer
  // boundary (~9e15 minor units), so verify the exact total in BigInt. Any drift
  // becomes a loud throw, never a silently wrong split.
  let checkSum = 0n;
  for (const p of out) checkSum += BigInt(p.minorUnits);
  if (checkSum !== BigInt(whole)) {
    throw new InvalidMoneyError("allocate could not preserve the total exactly (magnitude too large)");
  }
  return out;
}

/** Split into `n` as-equal-as-possible parts that sum exactly to `m`. */
export function split(m: Money, n: number): Money[] {
  if (!Number.isInteger(n) || n <= 0) {
    throw new InvalidMoneyError(`split count must be a positive integer, got ${n}`);
  }
  return allocate(m, new Array(n).fill(1));
}

/**
 * `total` minus the sum of `parts`, so [...parts, residual(total, parts)] sum
 * EXACTLY to total. Use to derive the final leg of a decomposition (seller net =
 * gross - fee - tax; discount line = gross - discountedTotal) so independently
 * rounded components always reconcile to the penny.
 */
export function residual(total: Money, parts: Money[]): Money {
  const taken = parts.length > 0 ? sum(parts, total.currency) : zero(total.currency);
  return subtract(total, taken);
}

// --- Comparison -------------------------------------------------------------

export function compare(a: Money, b: Money): -1 | 0 | 1 {
  assertSameCurrency(a, b);
  return a.minorUnits < b.minorUnits ? -1 : a.minorUnits > b.minorUnits ? 1 : 0;
}
export const equals = (a: Money, b: Money): boolean =>
  a.currency === b.currency && a.minorUnits === b.minorUnits;
export const greaterThan = (a: Money, b: Money): boolean => compare(a, b) > 0;
export const greaterThanOrEqual = (a: Money, b: Money): boolean => compare(a, b) >= 0;
export const lessThan = (a: Money, b: Money): boolean => compare(a, b) < 0;
export const lessThanOrEqual = (a: Money, b: Money): boolean => compare(a, b) <= 0;
export const min = (a: Money, b: Money): Money => (compare(a, b) <= 0 ? a : b);
export const max = (a: Money, b: Money): Money => (compare(a, b) >= 0 ? a : b);

// --- Formatting -------------------------------------------------------------

/**
 * Locale-aware display of the amount IN ITS OWN currency (no FX conversion).
 * Intl applies the correct fraction digits per currency (JPY 0, USD 2).
 */
export function format(
  m: Money,
  opts?: { locale?: string } & Omit<Intl.NumberFormatOptions, "style" | "currency">,
): string {
  const { locale, ...rest } = opts ?? {};
  return new Intl.NumberFormat(locale ?? "en-US", {
    style: "currency",
    currency: m.currency,
    ...rest,
  }).format(toDecimalNumber(m));
}

// --- Provider boundary ------------------------------------------------------

/**
 * Amount to hand the payment provider, which expects an integer
 * in the currency's smallest unit. For our set this equals minorUnits.
 * CAVEAT: some processors require HUF/TWD/UGX to be whole-major-unit multiples
 * (minorUnits % 100 === 0). We only support HUF of those; enforce that at the
 * provider call site, not here.
 */
export function toProviderAmount(m: Money): { amount: number; currency: string } {
  return { amount: m.minorUnits, currency: m.currency.toLowerCase() };
}

/** Reconstruct Money from a provider's integer minor-unit amount. */
export function fromProviderAmount(amount: number, currency: string): Money {
  const upper = typeof currency === "string" ? currency.toUpperCase() : currency;
  if (!isSupportedCurrency(upper)) {
    throw new InvalidMoneyError(`Unsupported provider currency: ${String(currency)}`);
  }
  return money(amount, upper);
}

// --- Serialization / validation ---------------------------------------------

export function isMoney(v: unknown): v is Money {
  return (
    typeof v === "object" && v !== null &&
    typeof (v as Money).minorUnits === "number" &&
    Number.isSafeInteger((v as Money).minorUnits) &&
    isSupportedCurrency((v as Money).currency)
  );
}

/** Plain JSON/Firestore object for a Money (already the shape, kept explicit). */
export function toJSON(m: Money): { minorUnits: number; currency: CurrencyCode } {
  return { minorUnits: m.minorUnits, currency: m.currency };
}

/** Runtime-validate an untrusted value (Firestore read, API body) into Money. */
export function fromUnknown(raw: unknown): Money {
  if (typeof raw !== "object" || raw === null) {
    throw new InvalidMoneyError("Money must be an object");
  }
  const obj = raw as Record<string, unknown>;
  const minorUnits = obj.minorUnits;
  const currency = obj.currency;
  if (typeof minorUnits !== "number") {
    throw new InvalidMoneyError("Money.minorUnits must be a number");
  }
  if (!isSupportedCurrency(currency)) {
    throw new InvalidMoneyError(`Money.currency unsupported: ${String(currency)}`);
  }
  return money(minorUnits, currency);
}
